/**
 * Copyright SimVentions, Inc. Usage, distribution, transferal, and licensing
 * of this source code is protected under SBIR law as described in DFARS 252.227-7018.
 *
 * SBIR data rights fully described in the README.md file in the top level directory of this project.
 */
import React, { CSSProperties, ReactNode } from "react";
import { IconType } from "react-icons";
import { useDexterTheme } from "../../DesignSystem/Theme/ThemeContext";

export interface EmptyStateProps {
  /** A react-icons component (e.g. FiUpload) or any custom node */
  icon?: IconType | ReactNode;
  title?: ReactNode;
  description?: ReactNode;
  /** Buttons, uploaders, links… rendered in a row below the text */
  actions?: ReactNode;
  /** Anything extra, rendered after the actions */
  children?: ReactNode;
  /** Max width of the description text (default 360px) */
  descriptionMaxWidth?: CSSProperties["maxWidth"];
  style?: CSSProperties;
  "data-testid"?: string;
}

export function EmptyPageComponent({
  icon,
  title,
  description,
  actions,
  children,
  descriptionMaxWidth = "360px",
  style,
  "data-testid": testId,
}: EmptyStateProps): JSX.Element {
  const { currentTheme } = useDexterTheme();

  const renderIcon = (): ReactNode => {
    if (!icon) return null;
    if (typeof icon === "function") {
      const Icon = icon as IconType;
      return <Icon size={22} color={currentTheme.colors.secondaryTextColor} />;
    }
    // Already an element, e.g. <FiUpload size={30} />
    return icon as ReactNode;
  };

  return (
    <div
      data-testid={testId}
      style={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: "16px",
        textAlign: "center",
        fontFamily: currentTheme.fonts.primary,
        backgroundColor: currentTheme.colors.pageBackgroundColor,
        ...style,
      }}
    >
      {icon && (
        <div
          style={{
            width: "56px",
            height: "56px",
            borderRadius: "50%",
            background: currentTheme.colors.backgroundTertiary,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {renderIcon()}
        </div>
      )}

      {(title || description) && (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: "6px",
          }}
        >
          {title && (
            <div
              style={{
                fontSize: "1.05rem",
                fontWeight: 700,
                color: currentTheme.colors.text,
              }}
            >
              {title}
            </div>
          )}
          {description && (
            <div
              style={{
                fontSize: "0.9rem",
                color: currentTheme.colors.secondaryTextColor,
                maxWidth: descriptionMaxWidth,
                lineHeight: 1.5,
              }}
            >
              {description}
            </div>
          )}
        </div>
      )}

      {actions && (
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            justifyContent: "center",
            gap: "10px",
            marginTop: "8px",
            width: "100%",
          }}
        >
          {actions}
        </div>
      )}

      {children}
    </div>
  );
}

export interface EmptyStateButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: "primary" | "secondary";
  icon?: IconType;
  id?: string;
}

export function EmptyStateButton({
  variant = "primary",
  icon: Icon,
  id,
  children,
  style,
  ...rest
}: EmptyStateButtonProps): JSX.Element {
  const { currentTheme } = useDexterTheme();
  const isPrimary = variant === "primary";

  return (
    <button
      data-testid={id}
      type="button"
      {...rest}
      style={{
        display: "flex",
        alignItems: "center",
        gap: "8px",
        background: isPrimary
          ? currentTheme.colors.buttonPrimaryColor
          : currentTheme.colors.buttonSecondaryColor,
        color: isPrimary
          ? currentTheme.colors.primaryElementFontColor
          : currentTheme.colors.text,
        border: "none",
        borderRadius: "8px",
        padding: "9px 16px",
        fontFamily: currentTheme.fonts.primary,
        fontWeight: 600,
        fontSize: "0.9rem",
        cursor: rest.disabled ? "not-allowed" : "pointer",
        opacity: rest.disabled ? 0.6 : 1,
        ...style,
      }}
    >
      {Icon && <Icon size={16} />}
      {children}
    </button>
  );
}

export default EmptyPageComponent;
